globalThis.CMFitMath = (() => {
  const bands = { bust: [4, 10], waist: [2, 6], hips: [4, 10] };
  const comparisonMax = { bust: 14, waist: 8, hips: 14 };
  const normalize = value => String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ł/g, 'l').toLowerCase().trim();
  const aliases = {
    bust: ['biust', 'bust', 'brust', 'brustumfang', 'prsa', 'obvod hrudniku', 'buste', 'busto', 'груди'],
    waist: ['talia', 'waist', 'taille', 'taillenumfang', 'pas', 'obvod pasu', 'talie', 'cintura', 'талія'],
    hips: ['biodra', 'hips', 'hufte', 'huften', 'huftumfang', 'boky', 'obvod boku', 'solduri', 'hanches', 'cadera', 'caderas', 'стегна']
  };
  function chartRows(raw, sizes, options = {}) {
    if (!Array.isArray(raw) || raw.length < 3 || options.blocked) return [];
    const table = raw.map(row => String(row).split(';').map(cell => cell.trim()));
    const measurements = {};
    for (const [key, names] of Object.entries(aliases)) measurements[key] = table.find(row => names.includes(normalize(row[0])));
    if (!measurements.bust || !measurements.waist) return [];
    const skirt = table.find(row => /spodniczka|skirt waist|taillenumfang.*rock|obvod pasu.*sukne|circumferinta taliei.*fusta/.test(normalize(row[0])));
    const numeric = cell => {
      const value = String(cell ?? '').replace(',', '.').trim();
      return /^\d{2,3}(\.\d{1,2})?$/.test(value) ? Number(value) * (options.scale || 1) : null;
    };
    const result = [];
    for (let column = 1; column < table[0].length; column++) {
      const label = table[0][column];
      if (options.colourChart) {
        const suffix = normalize(label.split('·')[1] || '');
        const eggplant = /baklazan|aubergine|eggplant|berenjena|vinete|lilek|баклажан/.test(suffix);
        const rest = /pozostale|other|autres|andere|ostatni|celelalte|otros|інші/.test(suffix);
        if ((!eggplant && !rest) || eggplant !== options.eggplant) continue;
      } else if (label.includes('·')) continue;
      const number = label.match(/^\d{2,3}(?=\s|$|\()/)?.[0];
      const size = sizes.find(size => size === label || size === number);
      if (!size) continue;
      const row = { size, bust: numeric(measurements.bust[column]), waist: numeric(measurements.waist[column]), hips: numeric(measurements.hips?.[column]) };
      if (skirt) {
        const skirtWaist = numeric(skirt[column]);
        if (!skirtWaist) continue;
        row.waist = Math.min(row.waist, skirtWaist);
      }
      if (!(row.bust >= 65 && row.bust <= 260)) continue;
      if (row.waist !== null && !(row.waist >= 50 && row.waist <= 240)) continue;
      if (row.hips !== null && !(row.hips >= 65 && row.hips <= 280)) continue;
      if (row.waist === null && row.hips === null) continue;
      result.push(row);
    }
    if (new Set(result.map(row => row.size)).size !== result.length) return [];
    return result.sort((a, b) => Number(a.size) - Number(b.size));
  }
  function assess(rows, body) {
    const rated = rows.map(row => {
      const ease = Object.fromEntries(Object.keys(bands).filter(key => Number.isFinite(row[key]) && Number.isFinite(body[key])).map(key => [key, Math.round((row[key] - body[key]) * 100) / 100]));
      const core = Number.isFinite(ease.bust) && Object.keys(ease).length >= 2 && Object.entries(ease).every(([key, value]) => value >= bands[key][0] && value <= bands[key][1]);
      const score = ((ease.bust - 6) / 3) ** 2 + (ease.waist !== undefined ? ((ease.waist - 4) / 2) ** 2 : 0) + (ease.hips !== undefined ? ((ease.hips - 6) / 3) ** 2 : 0);
      return { ...row, ease, core, score };
    });
    const preferred = rated.filter(row => row.core).sort((a, b) => a.score - b.score);
    if (preferred.length) {
      const choices = preferred.filter((row, index) => index === 0 || (index === 1 && row.score - preferred[0].score <= 0.5 && Math.abs(Number(row.size) - Number(preferred[0].size)) === 2));
      return { kind: choices.length === 1 ? 'estimate_match' : 'estimate_compare', cards: choices.map(row => ({ ...row, selectable: true })) };
    }
    const roomier = rated.filter(row => Number.isFinite(row.ease.bust) && Object.keys(row.ease).length >= 2 && Object.entries(row.ease).every(([key, value]) => value >= bands[key][0] && value <= comparisonMax[key])).sort((a, b) => a.score - b.score);
    if (roomier.length) {
      const choice = roomier[0];
      const previous = rated.find(row => Number(row.size) === Number(choice.size) - 2 && Object.entries(row.ease).some(([key, value]) => value < bands[key][0]));
      return { kind: 'estimate_try', cards: [{ ...choice, selectable: true, tryOn: true }], previous };
    }
    return { kind: 'estimate_outside', cards: [] };
  }
  return { bands, chartRows, assess };
})();

if (!customElements.get('cm-size-finder')) {
  class CMSizeFinder extends HTMLElement {
    static measurements = new Map();
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('[data-fit-form]');
      this.result = this.querySelector('[data-fit-result]');
      this.applyButton = this.querySelector('[data-fit-apply]');
      this.fields = [...this.form.querySelectorAll('input')];
      this.cards = this.querySelector('[data-fit-cards]');
      this.revision = 0;
      this.groups = JSON.parse(this.querySelector('[data-fit-options]').textContent);
      try { this.profile = JSON.parse(this.dataset.profile); } catch { this.profile = null; }
      try { this.chart = JSON.parse(this.dataset.chart); } catch { this.chart = null; }
      const remembered = CMSizeFinder.measurements.get(this.dataset.productId);
      if (remembered) this.fields.forEach(input => { input.value = remembered[input.name] || ''; });
      this.querySelector('[data-fit-open]').hidden = false;
      this.querySelector('[data-fit-open]').addEventListener('click', () => this.open());
      this.querySelector('[data-fit-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('close', () => { document.documentElement.classList.remove('cm-fit-scroll-lock'); this.controller?.abort(); });
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      });
      this.addEventListener('change', event => event.stopPropagation());
      this.form.addEventListener('submit', event => { event.preventDefault(); event.stopPropagation(); this.calculate(); });
      this.form.addEventListener('input', () => {
        this.revision++;
        this.controller?.abort();
        this.result.hidden = true;
        this.recommendation = null;
        this.fields.forEach(input => input.removeAttribute('aria-invalid'));
        this.querySelector('[data-fit-error]').hidden = true;
        CMSizeFinder.measurements.set(this.dataset.productId, Object.fromEntries(this.fields.map(input => [input.name, input.value])));
      });
      this.applyButton.addEventListener('click', () => this.apply());
      this.cards.addEventListener('click', event => {
        const button = event.target.closest('[data-choose-size]');
        if (!button) return;
        this.recommendation = button.dataset.chooseSize;
        this.apply();
      });
      this.querySelector('[data-fit-chart]')?.addEventListener('click', event => {
        const chart = document.getElementById(event.currentTarget.dataset.chartId);
        if (!chart?.open) return;
        this.dialog.close();
        chart.open(this.querySelector('[data-fit-open]'));
      });
    }
    disconnectedCallback() {
      this.controller?.abort();
      if (this.dialog?.open) { this.dialog.close(); document.documentElement.classList.remove('cm-fit-scroll-lock'); }
    }
    picker() { return this.closest('variant-selects'); }
    control(id) { return [...this.picker().querySelectorAll('input[data-option-value-id], option[data-option-value-id]')].find(el => el.dataset.optionValueId === String(id)); }
    selection() {
      return this.groups.map(group => ({ position: group.position, value: group.values.find(value => {
        const control = this.control(value.id);
        return control && (control.tagName === 'OPTION' ? control.selected : control.checked);
      }) }));
    }
    getRanges() {
      const p = this.profile;
      if (!p || p.version !== 1 || p.verified !== true || p.basis !== 'body' || p.units !== 'cm' || String(p.product_id) !== this.dataset.productId || p.size_option_position !== Number(this.dataset.sizePosition) || !p.verified_at || !p.source || !Array.isArray(p.profiles)) return null;
      const others = this.selection().filter(option => option.position !== p.size_option_position);
      if (others.some(option => !option.value)) return null;
      const profiles = p.profiles.filter(profile => profile.all_other_options === true || (Array.isArray(profile.option_value_ids) && profile.option_value_ids.length === others.length && others.every(option => profile.option_value_ids.map(String).includes(String(option.value.id)))));
      if (profiles.length !== 1) return null;
      const rows = profiles[0].ranges;
      const sizes = this.groups.find(group => group.position === p.size_option_position)?.values || [];
      if (!Array.isArray(rows) || !rows.length || new Set(rows.map(row => row.size)).size !== rows.length) return null;
      if (!rows.every(row => typeof row.size === 'string' && sizes.some(size => size.name === row.size) && ['bust', 'waist', 'hips'].every(key => Array.isArray(row[key]) && row[key].length === 2 && row[key].every(value => Number.isFinite(value) && value > 0) && row[key][0] <= row[key][1]))) return null;
      return rows;
    }
    getGarments() {
      const sizes = this.groups.find(group => group.position === Number(this.dataset.sizePosition))?.values.map(value => value.name) || [];
      const colour = this.selection().find(group => group.position !== Number(this.dataset.sizePosition))?.value;
      const colourChart = this.dataset.chartHandle === 'tabela-naomi-nowe-20261013';
      if (colourChart && !colour) return [];
      return CMFitMath.chartRows(this.chart, sizes, {
        blocked: this.dataset.estimateBlocked === 'true',
        scale: Number(this.dataset.measurementScale) || 1,
        colourChart,
        eggplant: String(colour?.id) === '9575927284053'
      });
    }
    open() {
      const verified = this.getRanges();
      const garments = verified ? [] : this.getGarments();
      this.estimate = !verified;
      const ready = !!verified || garments.length > 0;
      this.fields.forEach(input => {
        const required = !!verified || garments.some(row => Number.isFinite(row[input.name]));
        input.required = required;
        input.disabled = !required;
        input.closest('label').hidden = !required;
      });
      const partial = this.fields.some(input => input.disabled);
      this.querySelector('.cm-fit-inputs').classList.toggle('cm-fit-two', partial);
      this.querySelector('[data-fit-assumption]').hidden = !this.estimate || !ready;
      this.querySelector('[data-fit-partial]').hidden = !partial || !ready;
      this.querySelector('[data-fit-estimate-note]').hidden = !this.estimate;
      this.form.hidden = !ready;
      this.querySelector('[data-fit-unavailable]').hidden = ready;
      this.result.hidden = true;
      this.querySelector('[data-fit-error]').hidden = true;
      this.recommendation = null;
      this.dialog.showModal();
      document.documentElement.classList.add('cm-fit-scroll-lock');
      this.dialog.querySelector('h2').focus();
    }
    text(key, values = {}) {
      let text = this.querySelector(`[data-message="${key}"]`)?.content.textContent || '';
      Object.entries(values).forEach(([key, value]) => { text = text.replaceAll(`{${key}}`, String(value)); });
      return text;
    }
    message(key, size = '') {
      this.result.hidden = false;
      this.cards.replaceChildren();
      this.querySelector('[data-fit-message]').textContent = this.text(key, { size });
      this.applyButton.hidden = key !== 'match';
      this.querySelector('[data-fit-size]').textContent = size;
      this.querySelector('[data-fit-contact]').hidden = !['estimate_try', 'estimate_outside', 'no_match', 'ambiguous', 'sold_out', 'check_failed'].includes(key);
      this.result.focus();
    }
    showEstimate(assessment) {
      this.message(assessment.kind);
      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'pl', { maximumFractionDigits: 2 });
      assessment.cards.forEach(card => {
        const article = document.createElement('article');
        article.className = 'cm-fit-card';
        const title = document.createElement('h3');
        title.textContent = this.text('size_label', { size: card.size });
        article.append(title);
        const list = document.createElement('dl');
        for (const [key, ease] of Object.entries(card.ease)) {
          const row = document.createElement('div');
          const term = document.createElement('dt');
          term.textContent = this.text(key);
          const value = document.createElement('dd');
          const label = ease < CMFitMath.bands[key][0] ? 'less_room' : ease > CMFitMath.bands[key][1] ? 'more_room' : 'preferred_room';
          value.textContent = this.text('ease_value', { ease: `${ease > 0 ? '+' : ''}${formatter.format(ease)}` }) + ' · ' + this.text(label);
          row.append(term, value); list.append(row);
        }
        article.append(list);
        if (card.tryOn) {
          const note = document.createElement('p');
          note.className = 'cm-fit-try-note';
          const areas = Object.entries(card.ease).filter(([key, ease]) => ease > CMFitMath.bands[key][1]).map(([key]) => this.text(key)).join(', ');
          note.textContent = this.text('try_note', { areas });
          article.append(note);
        }
        if (!Number.isFinite(card.hips)) {
          const note = document.createElement('p'); note.className = 'cm-fit-card-note'; note.textContent = this.text('hips_unchecked'); article.append(note);
        }
        if (!Number.isFinite(card.waist)) {
          const note = document.createElement('p'); note.className = 'cm-fit-card-note'; note.textContent = this.text('waist_unchecked'); article.append(note);
        }
        if (card.selectable) {
          const button = document.createElement('button'); button.type = 'button'; button.className = 'cm-fit-button cm-fit-primary cm-fit-wide';
          button.dataset.chooseSize = card.size; button.textContent = this.text('choose_size', { size: card.size }); article.append(button);
        } else {
          const note = document.createElement('p'); note.className = 'cm-fit-card-note'; note.textContent = this.text(card.disproportion ? 'proportions_note' : 'tight_note'); article.append(note);
        }
        this.cards.append(article);
      });
      if (assessment.previous) {
        const note = document.createElement('p');
        note.className = 'cm-fit-smaller-note';
        const details = Object.entries(assessment.previous.ease).filter(([key, ease]) => ease < CMFitMath.bands[key][0]).map(([key, ease]) => `${this.text(key)}: ${this.text('ease_value', { ease: formatter.format(ease) })}`).join(', ');
        note.textContent = this.text('smaller_note', { size: assessment.previous.size, details });
        this.cards.append(note);
      }
      this.result.focus();
      this.result.style.scrollMarginTop = `${this.querySelector('.cm-fit-top').offsetHeight + 12}px`;
      this.result.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
    calculate() {
      this.revision++;
      this.controller?.abort();
      const verified = this.getRanges();
      const rows = verified || this.getGarments();
      if (!rows?.length) { this.form.hidden = true; this.querySelector('[data-fit-unavailable]').hidden = false; return; }
      const dimensions = {};
      let firstInvalid;
      for (const input of this.fields) {
        if (input.disabled) continue;
        const raw = input.value.trim().replace(',', '.');
        const value = /^\d{1,3}(\.\d{1,2})?$/.test(raw) ? Number(raw) : NaN;
        const valid = Number.isFinite(value) && value >= 30 && value <= 250;
        input.setAttribute('aria-invalid', String(!valid));
        if (!valid && !firstInvalid) firstInvalid = input;
        dimensions[input.name] = value;
      }
      this.querySelector('[data-fit-error]').hidden = !firstInvalid;
      this.recommendation = null;
      if (firstInvalid) { this.result.hidden = true; firstInvalid.focus(); return; }
      if (!verified) { this.showEstimate(CMFitMath.assess(rows, dimensions)); return; }
      const matches = rows.filter(row => Object.entries(dimensions).every(([key, value]) => value >= row[key][0] && value <= row[key][1]));
      if (matches.length !== 1) { this.message(matches.length ? 'ambiguous' : 'no_match'); return; }
      this.recommendation = matches[0].size;
      this.message('match', this.recommendation);
    }
    async apply() {
      if (!this.recommendation || this.applyButton.disabled) return;
      const size = this.recommendation;
      const revision = this.revision;
      const selection = this.selection();
      const targetValue = this.groups.find(group => group.position === Number(this.dataset.sizePosition))?.values.find(value => value.name === size);
      const control = targetValue && this.control(targetValue.id);
      if (!control || selection.some(option => !option.value)) { this.message('check_failed'); return; }
      this.applyButton.disabled = true;
      this.cards.querySelectorAll('button').forEach(button => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
      this.applyButton.setAttribute('aria-busy', 'true');
      this.controller = new AbortController();
      try {
        const url = new URL(this.dataset.productUrl, location.origin);
        url.searchParams.set('section_id', this.dataset.section);
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        url.searchParams.set('option_values', selection.map(option => option.position === Number(this.dataset.sizePosition) ? targetValue.id : option.value.id).join(','));
        const response = await fetch(url, { signal: this.controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('variant request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const variantText = html.getElementById(`variant-selects-${this.dataset.section}`)?.querySelector('[data-selected-variant]')?.textContent;
        const variant = variantText ? JSON.parse(variantText) : null;
        if (!this.isConnected || revision !== this.revision || this.recommendation !== size || !this.dialog.open) return;
        if (JSON.stringify(this.selection()) !== JSON.stringify(selection)) { this.message('check_failed'); return; }
        if (!variant || variant.options?.[Number(this.dataset.sizePosition) - 1] !== size) { this.message('check_failed'); return; }
        if (!variant.available) { this.message('sold_out', size); return; }
        this.dialog.close();
        if (control.tagName === 'OPTION') {
          control.parentElement.value = control.value;
          control.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
          control.parentElement.focus();
        } else {
          control.checked = true;
          control.dispatchEvent(new Event('change', { bubbles: true }));
          control.focus();
        }
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) this.message('check_failed');
      } finally {
        this.applyButton.disabled = false;
        this.applyButton.removeAttribute('aria-busy');
        this.cards.querySelectorAll('button').forEach(button => { button.disabled = false; button.removeAttribute('aria-busy'); });
      }
    }
  }
  customElements.define('cm-size-finder', CMSizeFinder);
}
